import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { fetchGameRange, fetchGameSummary, formatNumber } from "@/lib/api";
import { GAMES, blockStart } from "@/lib/games";
import VideoCard from "@/components/VideoCard";
import { LevelJumpBox } from "@/components/LevelJumpBox";
import usePageMeta from "@/hooks/use-page-meta";
import usePageJsonLd from "@/hooks/use-page-jsonld";

const GAME = GAMES["harry-potter"];

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#12141d] px-4 py-3">
      <div className="font-serif-editorial text-2xl sm:text-3xl text-[#f4f1ea]">{value}</div>
      <div className="mono-label text-white/40 mt-1">{label}</div>
    </div>
  );
}

function BlockPicker({ blocks, active, onPick }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="hp-block-picker">
      {blocks.map((b) => (
        <button
          key={b}
          type="button"
          onClick={() => onPick(b)}
          data-testid={`hp-block-${b}`}
          className={`px-3 py-1.5 rounded-full font-mono-tech text-[11px] tracking-widest border transition-colors ${
            b === active
              ? "bg-[#d95d39] border-[#d95d39] text-white"
              : "border-white/10 text-white/60 hover:border-[#d95d39]/60 hover:text-[#f4f1ea]"
          }`}
        >
          {b}–{b + 99}
        </button>
      ))}
    </div>
  );
}

function BlockGrid({ start }) {
  const { data, isLoading } = useSWR(`/levels/harry-potter/range/${start}`, () =>
    fetchGameRange(GAME.key, start, start + 99)
  );
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="aspect-video rounded-xl bg-white/[0.03] animate-pulse" />
        ))}
      </div>
    );
  }
  const items = data?.items || [];
  if (!items.length) return <p className="text-white/50 text-sm">No videos in this block yet.</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="hp-videos-grid">
      {items.map((v) => (
        <VideoCard key={v.id} video={{ ...v, category: "Harry Potter" }} />
      ))}
    </div>
  );
}

export default function HarryPotter() {
  const [params, setParams] = useSearchParams();
  const { data: summary } = useSWR("/levels/harry-potter/summary", () => fetchGameSummary(GAME.key));
  const max = summary?.max || 0;
  const blocks = max ? Array.from({ length: Math.ceil(max / 100) }, (_, i) => i * 100 + 1) : [];
  const active = blockStart(parseInt(params.get("block"), 10) || 1);

  usePageMeta({
    title: "Harry Potter: Puzzles & Spells — Every Level",
    description: `Harry Potter: Puzzles & Spells walkthroughs for ${summary ? summary.count.toLocaleString() : "thousands of"} levels, grouped in blocks of 100. Jump straight to any level.`,
    type: "website",
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  usePageJsonLd({
    key: "hp-collection",
    data: origin
      ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Harry Potter: Puzzles & Spells — Every Level",
          url: `${origin}${GAME.path}`,
        }
      : null,
  });

  const pick = (b) => {
    setParams({ block: String(b) });
    document.getElementById("hp-block-heading")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="hp-page">
      <div className="mb-10 sm:mb-14">
        <div className="mono-label">The spellbook shelf</div>
        <h1 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f4f1ea] mt-3">
          Harry Potter: Puzzles & Spells <span className="italic text-[#d95d39]">—</span> every level.
        </h1>
        <p data-testid="hp-lede" className="mt-4 sm:mt-5 max-w-2xl text-base sm:text-lg text-white/60 leading-relaxed">
          Every no-booster run from the Puzzles & Spells playlist, sorted by level and grouped
          in blocks of 100. {max ? `Levels 1 through ${max.toLocaleString()}.` : ""}
        </p>
        <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl">
          <Stat label="Levels" value={summary ? formatNumber(summary.count) : "—"} />
          <Stat label="Range" value={max ? `1–${max.toLocaleString()}` : "—"} />
          <Stat label="Blocks" value={blocks.length || "—"} />
        </div>
        <LevelJumpBox maxLevel={max} game={GAME.key} gameName={GAME.name} />
      </div>

      <BlockPicker blocks={blocks} active={active} onPick={pick} />

      <h2 id="hp-block-heading" className="scroll-mt-24 mt-10 mb-6 font-serif-editorial text-3xl sm:text-4xl text-[#f4f1ea]" data-testid="hp-block-heading">
        Levels {active}–{active + 99}
      </h2>
      <BlockGrid start={active} />
    </div>
  );
}
